import React, {
	memo,
	useState,
	useMemo,
	useCallback,
	forwardRef,
	useEffect,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import anim from "../../utils/anim";
import { ExpandableContainer, RoundedCard, ImageSelector } from "../CoreUI";

import { BsChevronLeft as LeftArrow } from "react-icons/bs";
import { FaChevronDown as DownArrow } from "react-icons/fa";
import { BiChevronRight as RightArrow } from "react-icons/bi";
import { IoIosShareAlt as ShareIcon } from "react-icons/io";
import { MdClose as CrossIcon } from "react-icons/md";

import "./surfaces-core-ui.css";
import useTrack, { eventTypes } from "../../hooks/useTrack";
import getImgUrl from "../../utils/getImgUrl";
import { DEFAULT_SHOP_IMG } from "../../conf";
import inPreview from "../../utils/inPreview";
import useDimensions from "../../hooks/useDimensions";
import genClassName from "../../utils/genClassName";
import accessibilityProvider from "../../utils/accessibilityProvider";

export const SurfaceTitle = memo(
	forwardRef(({ children, className = "", ...props }, ref) => (
		<h2 ref={ref} className={`surface-title ${className}`} {...props}>
			{children}
		</h2>
	))
);
export const SurfaceImageViewer = memo(
	({
		hidePrimaryImg = false,
		showBrandLogo = false,
		showBrandColor = false,
		data,
		imgArray,
		imgUrl = "",
		renderableContent,
		heightScale,
		direct = false,

		enableDrag = false,
		dragBreakPoint = 0,
		onDragComplete,
	}) => {
		const [activeImg, setActiveImg] = useState(0);
		const [, setInDrag] = useState(false);

		// const { innerHeight } = useContext(SurfaceDimensionsContext);
		const { innerHeight, inDesktopOnly } = useDimensions();

		const heightScaleComputed = useMemo(
			() => (heightScale ? heightScale : inDesktopOnly ? 1.68 : 1.93),
			[heightScale, inDesktopOnly]
		);

		const hasMultipleImages = useMemo(
			() => Array.isArray(imgArray) && imgArray.length > 1,
			[imgArray]
		);

		const handleImgPicker = useCallback((_, selectedImg) => {
			setActiveImg(selectedImg);
		}, []);

		const handleDragStart = useCallback(() => setInDrag(true), []);
		const handleDragEnd = useCallback(() => setInDrag(false), []);
		const handleDrag = useCallback(
			(_, info) => {
				const { offset: { y } = {} } = info || {};
				if (
					y < dragBreakPoint ||
					typeof onDragComplete !== "function"
				) {
					return;
				}
				onDragComplete();
			},
			[dragBreakPoint, onDragComplete]
		);

		useEffect(() => {
			setActiveImg(0);
		}, [imgArray]);

		if (activeImg >= imgArray?.length) {
			return null;
		}

		return (
			<motion.section
				className="surface-image-viewer-container"
				style={{
					height: innerHeight / heightScaleComputed,
					"--surface-image-viewer-container-height-scale":
						heightScaleComputed,
				}}
				variants={anim.dialogItem}
				role="group"
			>
				<section
					className={`surface-image-viewer ${
						showBrandLogo && data ? "surface-image-view-flexed" : ""
					}`}
					style={
						showBrandColor && data
							? {
									backgroundColor:
										data?.publisher_details?.modal_color ||
										"transparent",
							  }
							: {}
					}
					role="group"
				>
					{showBrandColor && (
						<section
							className="surface-image-viewer-brand-color-overlay"
							role="group"
						></section>
					)}
					{!hidePrimaryImg && (
						<motion.img
							src={
								hasMultipleImages
									? direct
										? imgArray[activeImg]
										: getImgUrl(imgArray[activeImg])
									: imgUrl
							}
							alt=""
							className="surface-image-viewer-primary-img"
							variants={anim.dialogItem}
							drag={enableDrag ? "y" : false}
							onDrag={handleDrag}
							layout
							dragConstraints={{
								top: 0,
								bottom: 0,
							}}
							onDragStart={handleDragStart}
							onDragEnd={handleDragEnd}
						/>
					)}
					{renderableContent}
					{showBrandLogo && data && (
						<motion.img
							src={
								data?.publisher_details?.logo_url ||
								DEFAULT_SHOP_IMG
							}
							className="surface-image-viewer-brand-logo"
							alt=""
							{...accessibilityProvider({
								disableTabIndex: true,
								name: "Brand logo",
								role: "button",
							})}
						/>
					)}
					{hasMultipleImages && !hidePrimaryImg && (
						<ImageSelector
							imgArray={imgArray}
							direct={direct}
							selected={activeImg}
							className="surface-image-viewer-img-picker"
							onChange={handleImgPicker}
						/>
					)}
				</section>
			</motion.section>
		);
	}
);

export const SurfaceRoundedCard = memo(
	forwardRef(
		({ className = "", children, showFullCard = false, ...props }, ref) => {
			return (
				<>
					<RoundedCard
						className={`surface-rounded-card ${
							showFullCard && "surface-rounded-card-full"
						} ${className}`}
						variants={anim.pdpItem}
						style={{
							minHeight: showFullCard ? `inherit` : "100px",
							borderRadius: showFullCard
								? "28px 28px 0px 0px"
								: "28px",
							...props?.style,
						}}
						ref={ref}
						{...props}
					>
						{children}
					</RoundedCard>
				</>
			);
		}
	)
);
export const SurfaceExpandableCard = memo(
	({ children, className = "", ...props }) => {
		return (
			<ExpandableContainer
				className={`surface-expandable-card ${className}`}
				{...props}
			>
				{children}
			</ExpandableContainer>
		);
	}
);

export const SurfaceNavBarButton = memo(
	({ children, className = "", transparent = false, ...props }) => {
		return (
			<div
				className={`${className} surface-nav-bar-button ${
					transparent ? "surface-nav-bar-button-transparent" : ""
				}`}
				{...accessibilityProvider({
					role: "button",
				})}
				{...props}
			>
				{children}
			</div>
		);
	}
);

export const SurfaceNavBarSection = memo(
	({ className = "", children, ...props }) => {
		return (
			<div className={`surface-nav-bar-section ${className}`} {...props}>
				{children}
			</div>
		);
	}
);

export const SurfaceNavBarBrandButton = memo(
	({
		heading,
		text,
		className = "",
		brandInfo,
		logoShadow = false,
		...props
	}) => (
		<SurfaceNavBarButton
			className={genClassName({
				base: "surface-nav-bar-brand-button",
				additional: className,
				conditionals: {
					"surface-nav-bar-brand-button-logo-shadow": logoShadow,
				},
			})}
			{...props}
		>
			<img src={brandInfo?.logo_url || DEFAULT_SHOP_IMG} alt="" />
			<section>
				<div>{heading}</div>
				<div>{text}</div>
			</section>
		</SurfaceNavBarButton>
	)
);

export const SurfaceNavBar = memo(
	({
		handleClose,
		className = "",
		product,
		children,
		moduleOrder = -1,
		useArrowForClose = false,
		...props
	}) => {
		const track = useTrack();
		const handleShare = useCallback(() => {
			const shareTitle = "Checkout this cool product I found";
			const shareText = product.name;
			const shareLink = product.landing_page;
			let navigator_api = false;
			if (navigator.share !== undefined) {
				navigator_api = true;
				navigator
					.share({
						title: shareTitle,
						text: shareText,
						url: shareLink,
					})
					.catch((e) => {
						console.log("An error occurred while sharing", e);
					});
			} else {
				const redirectUrl = `mailto:?subject=${shareTitle}&body=${shareText}%0A%0A${shareLink}`;
				if (window.parent) {
					return (window.parent.location.href = redirectUrl);
				}
				return (window.location.href = redirectUrl);
			}
			track(eventTypes.WIDGET_SURFACE_PRODUCT_SHARE, {
				product: JSON.stringify([product]),
				navigator_api,
				module_order: moduleOrder,
			});
		}, [product, track, moduleOrder]);

		return (
			<section className={`surface-nav-bar ${className}`} {...props}>
				<SurfaceNavBarSection>
					{useArrowForClose && (
						<SurfaceNavBarButton
							onClick={handleClose}
							className="surface-nav-bar-button-cross"
							{...accessibilityProvider({
								name: "Close",
							})}
						>
							<LeftArrow />
						</SurfaceNavBarButton>
					)}
					{product && !inPreview && (
						<SurfaceNavBarButton
							onClick={handleShare}
							{...accessibilityProvider({
								name: "Share",
							})}
						>
							<ShareIcon />
						</SurfaceNavBarButton>
					)}
					{!useArrowForClose && (
						<SurfaceNavBarButton
							onClick={handleClose}
							className="surface-nav-bar-button-cross"
							{...accessibilityProvider({
								name: "Close",
							})}
						>
							<CrossIcon />
						</SurfaceNavBarButton>
					)}
				</SurfaceNavBarSection>
				{children}
			</section>
		);
	}
);

export const SurfaceActionBarButton = memo(
	({ type = "theme", className = "", children, ...props }) => {
		return (
			<div
				className={genClassName({
					base: "surface-action-bar-button",
					conditionals: {
						"surface-action-bar-button-dark": type === "dark",
					},
					additional: className,
				})}
				{...accessibilityProvider({
					role: "button",
				})}
				{...props}
			>
				<div className="surface-action-bar-button-children">
					{children}
				</div>
				<div
					className="surface-action-bar-button-icon"
					{...accessibilityProvider({
						name: "Right arrow",
						role: "img",
					})}
				>
					<RightArrow />
				</div>
			</div>
		);
	}
);

export const SurfaceActionBarDummy = memo(
	({ className = "", children, icon, ...props }) => {
		return (
			<section
				className={`surface-action-bar-dummy ${className}`}
				{...props}
			>
				<div className="surface-action-bar-dummy-text">
					{children || "Select details"}
				</div>
				<div
					className="surface-action-bar-dummy-icon"
					{...accessibilityProvider({
						name: "Down arrow",
						role: "img",
					})}
				>
					{icon || <DownArrow />}
				</div>
			</section>
		);
	}
);

export const SurfaceActionBarItemImg = memo(({ className = "", ...props }) => (
	<img
		className={`surface-action-bar-item-img ${className}`}
		alt={props.alt || ""}
		{...props}
	/>
));

export const SurfaceActionBarItem = memo(
	({
		full = false,
		left = false,
		right = false,
		padded = false,
		children,
		className = "",
		...props
	}) => (
		<motion.section
			className={`surface-action-bar-item ${
				padded ? "surface-action-bar-item-padded" : ""
			} ${left ? "surface-action-bar-item-left" : ""} ${
				full ? "surface-action-bar-item-full" : ""
			} ${right ? "surface-action-bar-item-right" : ""} ${className}`}
			{...props}
		>
			{children}
		</motion.section>
	)
);

export const SurfaceActionBar = memo(
	({ children, fitChildren = false, small = false, ...props }) => {
		// const { innerHeight } = useContext(SurfaceDimensionsContext);
		const { innerHeight } = useDimensions();

		return (
			<motion.section
				className={`surface-action-bar ${
					fitChildren ? "surface-action-bar-fit-children" : ""
				} ${small ? "surface-action-bar-small" : ""}`}
				style={{ top: `${innerHeight - 90}px` }}
				{...props}
			>
				{children}
			</motion.section>
		);
	}
);

export const SurfaceActionOverlay = memo(
	({
		open = false,
		className = "",
		imgUrl = "",
		handleClose,
		children,
		...props
	}) => {
		// const { innerHeight, offsetTop } = useContext(SurfaceDimensionsContext);
		const { innerHeight, offsetTop } = useDimensions();

		if (!children) {
			return null;
		}
		return (
			<AnimatePresence>
				{open && (
					<motion.section
						className={`surface-action-overlay-container ${className}`}
						variants={anim.dialogQuick}
						initial="initial"
						animate="animate"
						exit="exit"
						style={{
							top: `${offsetTop}px`,
							height: `${innerHeight}px`,
							...(props?.style || {}),
						}}
						{...props}
					>
						<SurfaceNavBarButton
							onClick={handleClose}
							className="surface-action-overlay-dismiss-btn"
							variants={anim.pdpItem}
							{...accessibilityProvider({
								name: "Close",
							})}
						>
							<CrossIcon />
						</SurfaceNavBarButton>
						{imgUrl && (
							<motion.img
								src={imgUrl}
								alt=""
								className="surface-action-overlay-img"
								variants={anim.pdpItem}
							/>
						)}
						{/* <motion.div
					className="surface-action-overlay-dismiss-btn"
					onClick={handleClose}
					variants={anim.pdpItem}
				>
					CLOSE
				</motion.div> */}
						<RoundedCard
							className="surface-action-overlay-information"
							style={{ maxHeight: `${innerHeight / 1.8}px` }}
							variants={anim.pdpItem}
						>
							{children}
						</RoundedCard>
					</motion.section>
				)}
			</AnimatePresence>
		);
	}
);
